import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Globe, Lock, Check, Stamp } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

export const PublishButton = ({ kit, reload, disabled }) => {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const job = kit.publish_job || {};
  const checking = job.state === "running" && Date.now() - new Date(job.at).getTime() < 15 * 60000;
  const was = useRef(checking);
  useEffect(() => {
    if (was.current && !checking && job.state !== "running") {
      if (job.state === "done") toast.success(job.finalise ? "Pages checked — kit finalised, publish it whenever you like" : "Pages checked — published to the catalogue");
      else if (job.state === "blocked") toast.warning(`Not ${job.finalise ? "finalised" : "published"}: ${job.msg}`);
    }
    was.current = checking;
  }, [checking, job.state, job.msg, job.finalise]);
  if (kit.bespoke) {
    return <span data-testid="publish-private-label" className="inline-flex items-center gap-2 rounded-sm border border-stone-300 px-4 py-2 text-sm text-stone-500"><Lock size={14} /> Private bespoke kit</span>;
  }
  const live = !!kit.product?.published;
  const finalised = !!kit.finalised || live;
  const blockers = kit.summary?.blockers || 0;
  const fixes = kit.summary?.pending_fixes || 0;
  const pc = kit.summary?.page_check || {};
  const reason = finalised ? "" : kit.filling ? "Pages are still being filled — wait for it to finish (it clears itself within a few minutes if it was interrupted)."
    : disabled ? (kit.sections?.length ? "Wait until the kit finishes generating, checking or filling pages, then publish." : "This kit has no content yet.")
    : blockers ? `${blockers} venue(s) in the Verification tab still need review — open Verification → “Needs review” and Approve them, or press Fix & replace.`
    : fixes ? `${fixes} import fix(es) still need Approve or Undo (import summary at the top of the kit).`
    : !kit.images?.cover ? "Add a cover photo first — Photography tab → Generate or Upload on the Cover slot."
    : checking ? "Checking every page, then publishing — about a minute."
    : pc.fresh && pc.fails ? `${pc.fails} page problem(s) — ${pc.light} page(s) under 85% and ${pc.overflow} spilling over. Open the Page check tab to fix them.` : "";
  const finalise = async () => {
    setBusy(true);
    try {
      await http.post(`/kits/${kit.id}/check-and-publish?finalise=true`);
      toast.message("Checking this kit's pages — it's finalised automatically if they pass");
      was.current = true;
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggle = async () => {
    setBusy(true);
    try {
      await http.put(`/kits/${kit.id}/product`, { ...kit.product, published: !live });
      toast.success(live ? "Removed from the catalogue — still Ready" : "Published to the catalogue");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const unfinalise = async () => {
    if (!window.confirm(`Un-finalise ${kit.destination}?${live ? " It will also come off the live site." : ""} You'll need to finalise it again before publishing.`)) return;
    try { await http.post(`/kits/${kit.id}/unfinalise`); toast.success("Back to editing — finalise again when you're done"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      {finalised ? (
        <span className="inline-flex items-center gap-2">
          <button data-testid="publish-kit-button" onClick={() => (live ? setConfirm(true) : toggle())} disabled={busy}
            className={`inline-flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-bold transition-colors disabled:opacity-40 ${live ? "border border-emerald-500/50 text-emerald-700 hover:bg-emerald-50" : "bg-[#C9A847] text-white hover:bg-[#B8973A]"}`}>
            {live ? <><Check size={15} /> Published · Unpublish</> : <><Globe size={15} /> Publish</>}
          </button>
          <button data-testid="unfinalise-kit-button" onClick={unfinalise} className="text-xs text-stone-500 underline hover:text-stone-900">Un-finalise</button>
        </span>
      ) : (
        <button data-testid="finalise-kit-button" onClick={() => (reason ? toast.warning(reason) : finalise())} disabled={busy || kit.recheck?.state === "checking"} title={reason} aria-disabled={!!reason}
          className={`inline-flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-bold transition-colors disabled:opacity-40 bg-[#2B2A26] text-white hover:bg-black ${reason ? "opacity-50" : ""}`}>
          <Stamp size={15} /> {checking ? "Checking pages…" : blockers ? `Finalise (${blockers} to review)` : "Finalise kit"}
        </button>
      )}
      {reason && <span data-testid="publish-block-reason" className="basis-full text-xs text-amber-700 mt-1 max-w-xl">Can't finalise yet: {reason}</span>}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="unpublish-dialog" className="bg-white text-[#2B2A26]">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this kit from the catalogue?</AlertDialogTitle>
            <AlertDialogDescription>
              Buyers will no longer see it. It stays finalised, so you can publish it again at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unpublish-cancel" className="bg-white">Keep it live</AlertDialogCancel>
            <AlertDialogAction data-testid="unpublish-confirm" onClick={toggle} className="bg-red-600 text-white hover:bg-red-700">Unpublish</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
